import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Tabs,
  Table,
  Box,
  Button,
  Badge,
  Link,
  Alert
} from '@cloudscape-design/components';
import { useRole } from '../contexts/RoleContext';

interface ProductionOrder {
  orderId: string;
  designName: string;
  submissionDate: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  completionDate?: string;
  manufacturingFiles?: string[];
}

interface Subscription {
  productName: string;
  status: 'active' | 'expired' | 'cancelled';
  purchaseDate: string;
  expirationDate: string;
}

export const OrdersAndLicenses: React.FC = () => {
  const { isAdmin, isProduction, isLoading: roleLoading } = useRole();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<ProductionOrder | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // TODO: Call API to load orders and subscriptions
      // const ordersResponse = await apiService.get('/api/user/orders');
      // const subscriptionsResponse = await apiService.get('/api/user/subscriptions');
      
      // For now, use mock data
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setOrders([
        {
          orderId: 'ORD-001',
          designName: 'Chicago Sectional Map',
          submissionDate: '2026-03-01',
          status: 'completed',
          completionDate: '2026-03-05',
          manufacturingFiles: [
            'https://example.com/files/chicago-map.tif',
            'https://example.com/files/chicago-map.dxf'
          ]
        },
        {
          orderId: 'ORD-002',
          designName: 'VFR Wall Planning Chart',
          submissionDate: '2026-03-10',
          status: 'in_progress'
        }
      ]);
      
      setSubscriptions([
        {
          productName: 'METAR Data',
          status: 'active',
          purchaseDate: '2026-01-01',
          expirationDate: '2027-01-01'
        },
        {
          productName: 'TAF Data',
          status: 'expired',
          purchaseDate: '2025-01-01',
          expirationDate: '2026-01-01'
        }
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order: ProductionOrder) => {
    setSelectedOrder(order);
  };

  const handleDownloadFile = (fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  const handleRenewSubscription = async (productName: string) => {
    try {
      // TODO: Call API to renew subscription
      // await apiService.post('/api/user/subscriptions', { productName });
      
      alert(`Renewing subscription for ${productName}...`);
      await loadData();
    } catch (error) {
      console.error('Error renewing subscription:', error);
    }
  };

  const handlePurchaseProduct = async (productName: string) => {
    try {
      // TODO: Call API to purchase product
      // await apiService.post('/api/user/subscriptions', { productName });
      
      alert(`Purchasing ${productName}...`);
      await loadData();
    } catch (error) {
      console.error('Error purchasing product:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, 'green' | 'blue' | 'grey' | 'red'> = {
      active: 'green',
      pending: 'blue',
      in_progress: 'blue',
      completed: 'green',
      expired: 'red',
      cancelled: 'grey'
    };
    
    return (
      <Badge color={statusColors[status] || 'grey'}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Orders & Licenses</Header>
      
      <Tabs
        tabs={[
          {
            label: 'Production Orders',
            id: 'orders',
            content: (
              <Container>
                <Table
                  columnDefinitions={[
                    {
                      id: 'orderId',
                      header: 'Order ID',
                      cell: (item: ProductionOrder) => item.orderId
                    },
                    {
                      id: 'designName',
                      header: 'Design Name',
                      cell: (item: ProductionOrder) => item.designName
                    },
                    {
                      id: 'submissionDate',
                      header: 'Submission Date',
                      cell: (item: ProductionOrder) => new Date(item.submissionDate).toLocaleDateString()
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: (item: ProductionOrder) => getStatusBadge(item.status)
                    },
                    {
                      id: 'completionDate',
                      header: 'Completion Date',
                      cell: (item: ProductionOrder) => 
                        item.completionDate ? new Date(item.completionDate).toLocaleDateString() : '-'
                    },
                    {
                      id: 'actions',
                      header: 'Actions',
                      cell: (item: ProductionOrder) => (
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button onClick={() => handleViewOrder(item)}>
                            View Details
                          </Button>
                          {item.status === 'completed' && item.manufacturingFiles && (
                            <Button onClick={() => handleDownloadFile(item.manufacturingFiles![0])}>
                              Download Files
                            </Button>
                          )}
                        </SpaceBetween>
                      )
                    }
                  ]}
                  items={orders}
                  loading={loading}
                  loadingText="Loading orders..."
                  empty={
                    <Box textAlign="center" color="inherit">
                      <b>No orders</b>
                      <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                        You haven't submitted any production orders yet.
                      </Box>
                      <Button href="/print-map-maker">Create a Map</Button>
                    </Box>
                  }
                />
                
                {selectedOrder && (
                  <Box margin={{ top: 'l' }}>
                    <Container header={<Header variant="h3">Order Details</Header>}>
                      <SpaceBetween size="m">
                        <div>
                          <Box variant="awsui-key-label">Order ID</Box>
                          <div>{selectedOrder.orderId}</div>
                        </div>
                        <div>
                          <Box variant="awsui-key-label">Design Name</Box>
                          <div>{selectedOrder.designName}</div>
                        </div>
                        <div>
                          <Box variant="awsui-key-label">Status</Box>
                          <div>{getStatusBadge(selectedOrder.status)}</div>
                        </div>
                        {selectedOrder.manufacturingFiles && selectedOrder.manufacturingFiles.length > 0 && (
                          <div>
                            <Box variant="awsui-key-label">Manufacturing Files</Box>
                            <SpaceBetween size="xs">
                              {selectedOrder.manufacturingFiles.map((file, index) => (
                                <Link key={index} external href={file}>
                                  {file.split('/').pop()}
                                </Link>
                              ))}
                            </SpaceBetween>
                          </div>
                        )}
                      </SpaceBetween>
                    </Container>
                  </Box>
                )}
              </Container>
            )
          },
          {
            label: 'Data Product Licenses',
            id: 'licenses',
            content: (
              <Container>
                <Table
                  columnDefinitions={[
                    {
                      id: 'productName',
                      header: 'Product Name',
                      cell: (item: Subscription) => item.productName
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: (item: Subscription) => getStatusBadge(item.status)
                    },
                    {
                      id: 'purchaseDate',
                      header: 'Purchase Date',
                      cell: (item: Subscription) => new Date(item.purchaseDate).toLocaleDateString()
                    },
                    {
                      id: 'expirationDate',
                      header: 'Expiration Date',
                      cell: (item: Subscription) => new Date(item.expirationDate).toLocaleDateString()
                    },
                    {
                      id: 'actions',
                      header: 'Actions',
                      cell: (item: Subscription) => (
                        <SpaceBetween direction="horizontal" size="xs">
                          {item.status === 'expired' && (
                            <Button onClick={() => handleRenewSubscription(item.productName)}>
                              Renew
                            </Button>
                          )}
                          {item.status === 'active' && (
                            <Button disabled>Active</Button>
                          )}
                        </SpaceBetween>
                      )
                    }
                  ]}
                  items={subscriptions}
                  loading={loading}
                  loadingText="Loading subscriptions..."
                  empty={
                    <Box textAlign="center" color="inherit">
                      <b>No subscriptions</b>
                      <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                        You don't have any data product licenses yet.
                      </Box>
                      <Button onClick={() => handlePurchaseProduct('METAR Data')}>
                        Purchase License
                      </Button>
                    </Box>
                  }
                />
                
                <Box margin={{ top: 'l' }}>
                  <Alert type="info">
                    Data product licenses provide access to real-time weather data, aviation charts, and other premium features.
                  </Alert>
                </Box>
              </Container>
            )
          }
        ]}
      />
    </SpaceBetween>
  );
};

export default OrdersAndLicenses;
